import { useNavigate } from 'react-router-dom';
import { Shield, Eye, Info, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useConsent } from '@/lib/consent';
import { unloadAnalytics } from '@/lib/analytics';
import { updateErrorTrackingConsent } from '@/lib/errorTracking';
import { useToast } from '@/hooks/use-toast';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { PageLayout } from '@/components/Layout/PageLayout';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

export default function PrivacyAnalytics() {
  const navigate = useNavigate();
  const { consent, setConsent, revokeAll, resetBanner } = useConsent();
  const { toast } = useToast();
  const { success } = useHapticFeedback();

  const handleAnalyticsToggle = (checked: boolean) => {
    setConsent({ analytics: checked });
    success();
    
    toast({
      title: checked ? "Analytics enabled" : "Analytics disabled",
      description: checked 
        ? "Thank you for helping us improve!" 
        : "No further data will be collected.",
      duration: 2000,
    });
  };

  const handleCrashToggle = (checked: boolean) => {
    setConsent({ crash: checked });
    updateErrorTrackingConsent(checked);
    success();

    toast({
      title: checked ? 'Crash reporting enabled' : 'Crash reporting disabled',
      description: checked
        ? 'Thanks — this helps us fix bugs faster.'
        : 'No further error data will be collected.',
      duration: 2000,
    });
  };

  const handleDeleteAndOptOut = () => {
    revokeAll();
    unloadAnalytics();
    updateErrorTrackingConsent(false);
    success();
    
    toast({
      title: "Analytics disabled",
      description: "All tracking has been stopped.",
      duration: 2000,
    });
  };

  const handleResetConsent = () => {
    console.log('[PrivacyAnalytics] User clicked Reset Consent');
    unloadAnalytics();
    resetBanner();
    success();
    
    toast({
      title: "Consent reset",
      description: "The consent banner will appear on your next page load.",
      duration: 3000,
    });
    console.log('[PrivacyAnalytics] Reset complete, toast shown');
  };

  return (
    <PageLayout title="Privacy & Analytics" maxWidth="2xl">
      <div className="space-y-6">
        <Card className="p-6 space-y-6">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-primary shrink-0 mt-0.5" />
            <div>
              <h2 className="text-xl font-semibold mb-2">Anonymous Usage Analytics</h2>
              <p className="text-sm text-muted-foreground">
                Analytics are OFF by default. Enable to help us improve performance and reliability. 
                No cross-site tracking, no ads. You can change your choice anytime.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between py-4 border-y">
            <div className="flex-1">
              <Label htmlFor="analytics-toggle" className="text-base font-medium">
                Analytics (anonymous usage stats)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Help us improve with cookieless, privacy-friendly stats. Hosted in the EU.
              </p>
            </div>
            <Switch
              id="analytics-toggle"
              checked={consent.analytics}
              onCheckedChange={handleAnalyticsToggle}
              className="ml-4"
            />
          </div>

          <div className="flex items-center justify-between py-4 border-b">
            <div className="flex-1">
              <Label htmlFor="crash-toggle" className="text-base font-medium">
                Crash reporting (anonymous error reports)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Share anonymous error reports to help us fix bugs. Hosted in the EU.
              </p>
            </div>
            <Switch
              id="crash-toggle"
              checked={consent.crash}
              onCheckedChange={handleCrashToggle}
              className="ml-4"
            />
          </div>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="what-we-track">
              <AccordionTrigger className="text-base">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  What we track
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pt-2">
                <p className="text-sm text-muted-foreground">
                  We collect anonymous usage events to understand how you use the app:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• <strong>expense_saved</strong> - When you log an expense (category only, no amounts)</li>
                  <li>• <strong>voice_used</strong> - When you use voice input</li>
                  <li>• <strong>badges_opened</strong> - When you view your badges</li>
                  <li>• <strong>focus_mode_toggle</strong> - When you toggle calm mode</li>
                  <li>• <strong>app_opened</strong> - When you open the app</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                  <strong>Example:</strong> "expense_saved: &#123; category_id: 'food' &#125;"
                  <br />
                  <strong>We never track:</strong> Specific amounts, merchant names, or personal notes.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="crash-reporting">
              <AccordionTrigger className="text-base">
                <div className="flex items-center gap-2">
                  <Bug className="w-4 h-4" />
                  Crash reporting details
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pt-2">
                <p className="text-sm text-muted-foreground">
                  If enabled, we collect anonymous error information to improve stability:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Error messages and stack traces</li>
                  <li>• Browser type and version</li>
                  <li>• Session replays on errors (with all text masked)</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                  You can turn this off anytime using the toggle above.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="how-it-works">
              <AccordionTrigger className="text-base">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  How it works
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-2 pt-2 text-sm text-muted-foreground">
                <p>
                  We use <strong>Plausible Analytics</strong>, a privacy-friendly, cookieless analytics service:
                </p>
                <ul className="space-y-1 ml-4">
                  <li>✓ No cookies or persistent identifiers</li>
                  <li>✓ Data hosted in the EU (GDPR compliant)</li>
                  <li>✓ No cross-site tracking</li>
                  <li>✓ No data sold to third parties</li>
                  <li>✓ Aggregated statistics only</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="pt-4 border-t space-y-3">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleResetConsent}
            >
              Reset consent choice
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              This will show the consent banner again on your next visit.
            </p>
            
            <Button
              variant="destructive"
              className="w-full"
              onClick={handleDeleteAndOptOut}
              disabled={!consent.analytics && !consent.crash}
            >
              Delete identifiers & turn off
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              This will stop all optional tracking immediately.
            </p>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold mb-3">Legal & Privacy</h3>
          <div className="space-y-2 text-sm">
            <Button
              variant="link"
              className="h-auto p-0 text-primary"
              onClick={() => navigate('/privacy')}
            >
              Read our Privacy Policy →
            </Button>
            <br />
            <Button
              variant="link"
              className="h-auto p-0 text-primary"
              asChild
            >
              <a 
                href="https://plausible.io/privacy-focused-web-analytics" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                About Plausible Analytics ↗
              </a>
            </Button>
          </div>
        </Card>

        <div className="text-xs text-muted-foreground text-center">
          Status: Analytics {consent.analytics ? 'ON' : 'OFF'} · Crash reporting {consent.crash ? 'ON' : 'OFF'}
          {consent.timestamp && (
            <span className="ml-2">
              · Updated {new Date(consent.timestamp).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
